import './UserManagement.scss';
import '../Admin.scss';

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGetUserAdminDetails, useResolveUserId } from 'queryHooks/userAdmin';
import {
  Button,
  Switch,
  Input,
  Loader,
  LoadingPlaceholder,
  Menu,
  MenuItem,
  TableStatusChip,
  TableStatusChipIntent,
  Modal,
} from '@trackman/web-shared-components';
import { Colors } from '@trackman/web-shared-components';
import { useServices, useStores } from 'index';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { getErrorMessage } from 'utilities/ErrorHelper';
import { AdminCertificationStatus, CertificationAccessStatus } from 'types/admin/adminUserDetails';
import { ReactComponent as Copy } from 'images/icons/Copy.svg';
import { ReactComponent as BigSearch } from 'images/icons/BigSearch.svg';
import { ReactComponent as Search } from 'images/icons/Search.svg';
import { ReactComponent as Medal } from 'images/icons/MedalThin.svg';
import { ReactComponent as ThreeDots } from 'images/icons/ThreeDots.svg';
import { ProfileCard } from './ProfileCard/ProfileCard';

const STATUS_ORDER: CertificationAccessStatus[] = ['Completed', 'Purchased', 'NotPurchased'];

const STATUS_LABEL: Record<CertificationAccessStatus, string> = {
  Completed: 'Completed',
  Purchased: 'Purchased',
  NotPurchased: 'Not purchased',
  PartOfOrganisation: 'Part of Organisation'
};

const STATUS_INTENT: Record<CertificationAccessStatus, TableStatusChipIntent> = {
  Completed: 'success',
  Purchased: 'grey',
  NotPurchased: 'grey',
  PartOfOrganisation: 'grey'
};

export const UserManagement = () => {
  useSetPageTitle({ title: 'Users' });

  const service = useServices().manageUserDataService;
  const { notificationStore } = useStores();
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const [userId, setUserId] = useState<string | null>(null);

  const resolveUserId = useResolveUserId(setUserId);

  const handleSearch = () => {
    if (!searchInput.trim()) return;
    resolveUserId.mutate(searchInput);
  };

  const { data: userDetails, isLoading, isError } = useGetUserAdminDetails(userId);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['adminUserDetails', userId] });

  const grantTestAccess = useMutation({
    mutationFn: (certId: string) => service.grantCertificationTestAccess(userId!, certId),
    onSuccess: () => {
      notificationStore.addToast('success', 'Test access granted', 'success');
      invalidate();
    },
    onError: (e: Error) => notificationStore.addToast('error', getErrorMessage(e), 'error'),
  });

  const revokeTestAccess = useMutation({
    mutationFn: (certId: string) => service.revokeCertificationTestAccess(userId!, certId),
    onSuccess: () => {
      notificationStore.addToast('success', 'Test access revoked', 'success');
      invalidate();
    },
    onError: (e: Error) => notificationStore.addToast('error', getErrorMessage(e), 'error'),
  });

  const grantCertificate = useMutation({
    mutationFn: (certId: string) => service.grantCertificate(userId!, certId),
    onSuccess: () => {
      notificationStore.addToast('success', 'Certificate granted', 'success');
      invalidate();
    },
    onError: (e: Error) => notificationStore.addToast('error', getErrorMessage(e), 'error'),
  });

  const revokeCertificate = useMutation({
    mutationFn: (certId: string) => service.revokeCertificate(userId!, certId),
    onSuccess: () => {
      notificationStore.addToast('success', 'Certificate revoked', 'success');
      invalidate();
    },
    onError: (e: Error) => notificationStore.addToast('error', getErrorMessage(e), 'error'),
  });

  const sortedCerts = userDetails
    ? [...userDetails.certifications].sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status))
    : null;

  return (
    <>
      <div className='default-page-header user-management-header'>
        <div>
          <h1 className='page-title'>Users</h1>
          <span className='page-subtitle'>Manage your Trackman University users</span>
        </div>
        <div className='user-header-search'>
          <Input
            className='user-header-search-input'
            label='Search by email or User ID'
            value={searchInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.currentTarget.value)}
            onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            className='user-search-button'
            onClick={handleSearch}
            intent='primary'
            size='small'
            startIcon={<Search className='white-search-icon' />}
          />
        </div>
      </div>

      {!userId ? (
        <div className='user-empty-state'>
          <BigSearch />
          <strong className='user-empty-title'>Search for user</strong>
          <p className='user-empty-subtitle'>Search for a user in order to manage their access, status and certificates.</p>
          <div className='user-empty-search-wrap'>
            <Input
              label={
                <>
                  <Search className='search-icon' /> Search by email or User ID
                </>
              }
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              className='user-search-button'
              onClick={handleSearch}
              intent='primary'
              size='small'
              startIcon={<Search className='white-search-icon' />}
            />
          </div>
          {resolveUserId.isError && <div className='user-not-found'>User not found. Check the email or ID and try again.</div>}
        </div>
      ) : (
        <>
          {isLoading ? (
            <LoadingPlaceholder margin={50} />
          ) : (
            <div className='user-management'>
              <div className='user-management-certs'>
                <h2 className='certs-title'>Certificates</h2>
                {isError && <p className='certs-empty'>Failed to load user data.</p>}
                {sortedCerts &&
                  sortedCerts.map((cert) => (
                    <CertItem
                      key={cert.certificationId}
                      cert={cert}
                      onGrantTestAccess={() => grantTestAccess.mutate(cert.certificationId)}
                      onRevokeTestAccess={() => revokeTestAccess.mutate(cert.certificationId)}
                      onGrantCertificate={() => grantCertificate.mutate(cert.certificationId)}
                      onRevokeCertificate={() => revokeCertificate.mutate(cert.certificationId)}
                    />
                  ))}
              </div>
              {userDetails && <ProfileCard userId={userId} userDetails={userDetails} />}
            </div>
          )}
        </>
      )}
    </>
  );
};

type CertItemProps = {
  cert: AdminCertificationStatus;
  onGrantTestAccess: () => void;
  onRevokeTestAccess: () => void;
  onGrantCertificate: () => void;
  onRevokeCertificate: () => void;
};

const CertItem = ({ cert, onGrantTestAccess, onRevokeTestAccess, onGrantCertificate, onRevokeCertificate }: CertItemProps) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | { name: string; run: () => void }>(null);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
    setMenuOpen(true);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuOpen(false);
  };

  const revokeCertificate = () => {
    onRevokeCertificate();
    closeMenu();
  };

  const grantCertificate = () => {
    onGrantCertificate();
    closeMenu();
  };

  const revokeTestAccess = () => {
    onRevokeTestAccess();
    closeMenu();
  };

  const grantTestAccess = () => {
    onGrantTestAccess();
    closeMenu();
  };

  const openConfirm = (name: string, run: () => void) => {
    setPendingAction({ name, run });
    setDeleteDialogOpen(true);
  };

  const onDialogClose = (confirmed: boolean) => {
    setDeleteDialogOpen(false);
    closeMenu();
    if (confirmed) pendingAction?.run();
    setPendingAction(null);
  };

  return (
    <div className='cert-item'>
      <div className='cert-item-status'>
        <TableStatusChip
          label={STATUS_LABEL[cert.status]}
          intent={STATUS_INTENT[cert.status]}
          labelProps={
            cert.status === 'Purchased' || cert.status === 'PartOfOrganisation' ? { sx: { color: Colors.textElementGrey } } : undefined
          }
        />
      </div>
      <div className='cert-item-row'>
        <div className='cert-item-name'>
          <Medal />
          <span>{cert.title}</span>
        </div>
        <div className='cert-item-actions'>
          <Button className='cert-menu-btn' size='small' startIcon={<ThreeDots />} onClick={(event) => openMenu(event)} />
          <Menu className='cert-menu-list' open={isMenuOpen} anchorEl={menuAnchor} onClose={closeMenu}>
            <div>
              {cert.status === 'Completed' && (
                <MenuItem label='Revoke certificate' onClick={() => openConfirm('Revoke certificate', revokeCertificate)} />
              )}
              {cert.status === 'Purchased' && (
                <>
                  <MenuItem label='Grant certificate' onClick={grantCertificate} />
                  <MenuItem label='Revoke test access' onClick={() => openConfirm('Revoke test access', revokeTestAccess)} />
                </>
              )}
              {cert.status === 'NotPurchased' && (
                <>
                  <MenuItem label='Grant test access' onClick={grantTestAccess} />
                  <MenuItem label='Grant certificate' onClick={grantCertificate} />
                </>
              )}
            </div>
          </Menu>
          <RevokeConfirmationDialog open={deleteDialogOpen} actionName={pendingAction?.name ?? ''} onClose={onDialogClose} />
        </div>
      </div>
    </div>
  );
};

type ConfirmationDialogProps = {
  actionName: string;
  open: boolean;
  onClose: (confirmed: boolean) => void;
};

const RevokeConfirmationDialog = ({ open, actionName, onClose }: ConfirmationDialogProps) => {
  const handleClose = () => onClose(false);
  const handleConfirm = () => onClose(true);

  return (
    <Modal
      open={open}
      title={actionName}
      cancelBtn={{
        label: 'Cancel',
        onClick: handleClose,
      }}
      confirmBtn={{
        label: 'Confirm',
        intent: 'error',
        onClick: handleConfirm,
      }}
      onClose={handleClose}
    >
      <div>Are you sure you want to {actionName}?</div>
    </Modal>
  );
};

export default UserManagement;
